// V96.35 — Helper unifié pour appeler l'API admin de l'app cliente.
// Avant V96.35, chaque service (publishToClientApp, fetchClientFeedbacks, etc.)
// appelait directement `${VITE_CLIENT_APP_API_URL}/api/admin/foo` avec un
// `Authorization: Bearer ${VITE_CLIENT_APP_ADMIN_SECRET}` — ce secret était
// donc inliné dans le bundle JS public (audit V96.34 = risque sécu critique).
//
// Depuis V96.35, le secret vit côté serveur uniquement (Vercel env
// `CLIENT_APP_ADMIN_SECRET`, sans prefix VITE_). Ce helper passe par le proxy
// `/api/client-app-proxy` (voir api/client-app-proxy.js) qui ajoute le Bearer
// header server-side.
//
// Usage type :
//   await clientAppFetch('/api/admin/publish-plan', { method: 'POST', payload: {...} });
//   await clientAppFetch('/api/admin/clients-status', { method: 'GET', query: { client_ids: '...' } });
//
// VITE_CLIENT_APP_API_URL est conservé côté client uniquement pour les checks
// de configuration UI (ex: désactiver le bouton "Publier" si pas configuré) —
// l'URL n'est pas sensible (visible en dev tools network), seul le secret l'était.

import { supabase } from '../supabaseClient';

const ENV_API_URL = 'VITE_CLIENT_APP_API_URL';

function getEnv(key) {
  return import.meta.env?.[key];
}

export class ClientAppConfigError extends Error {
  constructor(message) {
    super(message);
    this.name = 'ClientAppConfigError';
  }
}

export class ClientAppHttpError extends Error {
  constructor(message, status, payload) {
    super(message);
    this.name = 'ClientAppHttpError';
    this.status = status;
    this.payload = payload;
  }
}

/**
 * Vérifie la config UI minimale. Le secret n'est plus checkable côté client
 * (il vit server-side) — seule l'URL est encore vérifiée pour gating UI.
 * Le proxy retournera 500 'non configuré' si CLIENT_APP_ADMIN_SECRET manque.
 */
export function checkClientAppConfig() {
  const apiUrl = getEnv(ENV_API_URL);
  const issues = [];
  if (!apiUrl) issues.push(`Variable d'env manquante : ${ENV_API_URL}`);
  return { ok: issues.length === 0, issues };
}

/**
 * Appelle un endpoint admin de l'app cliente via le proxy server-side.
 *
 * @param {string} path - chemin commençant par /api/admin/...
 * @param {object} [opts]
 * @param {string} [opts.method='POST']
 * @param {object|null} [opts.payload=null] - body JSON
 * @param {object|null} [opts.query=null]   - query string params
 * @param {object|null} [opts.proof=null]   - preuve serveur requise pour publish-plan
 * @param {number} [opts.timeoutMs=45000]   - délai au-delà duquel on abandonne
 * @returns {Promise<any>} - body JSON parsé de la réponse upstream
 * @throws {ClientAppConfigError|ClientAppHttpError}
 *
 * Revue ergo 2026-08-21 — cet appel n'avait aucun délai de garde. Quand le
 * proxy se figeait, la promesse ne se résolvait jamais : l'écran de
 * publication tournait indéfiniment, sans erreur, sans issue. Une requête qui
 * n'aboutit pas doit finir par le dire.
 *
 * Le message d'abandon ne prétend jamais que rien n'a été fait : côté
 * publication, la réponse peut se perdre alors que le serveur a bien écrit.
 * C'est précisément le cas que la relecture distante sait rattraper.
 */
export const CLIENT_APP_TIMEOUT_MS = 45000;

export async function clientAppFetch(
  path,
  {
    method = 'POST',
    payload = null,
    query = null,
    proof = null,
    timeoutMs = CLIENT_APP_TIMEOUT_MS,
  } = {},
  { auth = supabase, fetchApi = globalThis.fetch } = {},
) {
  if (typeof path !== 'string' || !path.startsWith('/api/admin/')) {
    throw new ClientAppHttpError('path invalide (doit commencer par /api/admin/)', 0, null);
  }

  let session;
  try {
    const result = await auth?.auth?.getSession?.();
    if (result?.error) throw result.error;
    session = result?.data?.session;
  } catch {
    session = null;
  }
  const accessToken = session?.access_token;
  if (!accessToken) {
    throw new ClientAppHttpError('Session requise', 401, null);
  }

  // Un AbortController borne l'attente. Sans lui, un proxy figé laissait
  // l'appelant suspendu pour toujours.
  const controleur = typeof AbortController === 'function' ? new AbortController() : null;
  const delai = (controleur && Number.isFinite(timeoutMs) && timeoutMs > 0)
    ? setTimeout(() => controleur.abort(), timeoutMs)
    : null;

  let res;
  try {
    res = await fetchApi('/api/client-app-proxy', {
      method: 'POST',
      headers: {
        authorization: `Bearer ${accessToken}`,
        'content-type': 'application/json',
      },
      body: JSON.stringify({ path, method, payload, query, proof }),
      ...(controleur ? { signal: controleur.signal } : {}),
    });
  } catch (err) {
    if (controleur?.signal?.aborted) {
      throw new ClientAppHttpError(
        `L’app cliente n’a pas répondu en ${Math.round(timeoutMs / 1000)} s. `
        + 'La demande a peut-être abouti de son côté : vérifie le statut avant de réessayer.',
        0,
        null,
      );
    }
    throw new ClientAppHttpError(`Erreur réseau : ${err?.message || err}`, 0, null);
  } finally {
    if (delai) clearTimeout(delai);
  }

  let body = null;
  try { body = await res.json(); } catch { /* body non-JSON, body=null */ }

  if (!res.ok) {
    const msg = body?.error || body?.message || `HTTP ${res.status}`;
    if (res.status === 500 && /non configur/i.test(msg)) {
      throw new ClientAppConfigError(msg);
    }
    throw new ClientAppHttpError(msg, res.status, body);
  }
  return body;
}
